import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDocs,
  serverTimestamp,
  updateDoc,
} from "firebase/firestore";
import { useEffect, useRef, useState } from "react";
import {
  EARTHQUAKE_SCENARIO_BLUEPRINT,
  FLOOD_SCENARIO_BLUEPRINT,
  INJECT_RED_INJURIES,
  jitterSimulationCoords,
} from "./demoData";
import { db } from "./firebase";

const TOAST_MS = 3500;

const getLocation = () =>
  new Promise((resolve) => {
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        resolve({
          lat: pos.coords.latitude,
          lon: pos.coords.longitude,
        });
      },
      () => {
        resolve({
          lat: 28.6139,
          lon: 77.209,
        });
      },
    );
  });

const buildPacket = (urgency, isAutoGenerated, location) => ({
  urgency,
  injury:
    urgency === "RED"
      ? "Severe crush injury"
      : urgency === "YELLOW"
        ? "Moderate trauma"
        : "Walking wounded",
  confidence: urgency === "RED" ? 0.95 : urgency === "YELLOW" ? 0.78 : 0.65,
  isAutoGenerated,
  createdAt: serverTimestamp(),
  lat: location.lat,
  lon: location.lon,
});

export default function VictimPanel({
  packets = [],
  peerCount = 0,
  demoMode = false,
  onSwitchToField,
}) {
  const [lastSent, setLastSent] = useState(null);
  const [sosState, setSosState] = useState("idle");
  const [simBusy, setSimBusy] = useState(false);
  const [toasts, setToasts] = useState([]);
  const toastTimers = useRef(new Map());
  const sosResetRef = useRef(null);

  useEffect(() => {
    const timeouts = toastTimers.current;
    return () => {
      timeouts.forEach((t) => clearTimeout(t));
      timeouts.clear();
      if (sosResetRef.current) clearTimeout(sosResetRef.current);
    };
  }, []);

  const pushToast = (message, urgency) => {
    const id = `${Date.now()}-${Math.random()}`;
    setToasts((prev) => [...prev, { id, message, urgency }]);
    const t = setTimeout(() => {
      setToasts((prev) => prev.filter((x) => x.id !== id));
      toastTimers.current.delete(id);
    }, TOAST_MS);
    toastTimers.current.set(id, t);
  };

  const writeSimulationPackets = async (blueprints) => {
    if (!db) return;
    setSimBusy(true);
    try {
      for (const row of blueprints) {
        const loc = jitterSimulationCoords();
        await addDoc(collection(db, "packets"), {
          urgency: row.urgency,
          injury: row.injury,
          confidence: row.confidence,
          isAutoGenerated: row.isAutoGenerated,
          signals: row.signals ?? "MOTION·AUDIO·VISION",
          createdAt: serverTimestamp(),
          lat: loc.lat,
          lon: loc.lon,
        });
      }
      pushToast(`Injected ${blueprints.length} packets`, "GREEN");
    } catch (e) {
      console.error(e);
      pushToast("Simulation write failed", "RED");
    } finally {
      setSimBusy(false);
    }
  };

  const handleEarthquakeScenario = () =>
    writeSimulationPackets(EARTHQUAKE_SCENARIO_BLUEPRINT);

  const handleFloodScenario = () => writeSimulationPackets(FLOOD_SCENARIO_BLUEPRINT);

  const handleInject5Red = async () => {
    if (!db) return;
    const blueprints = INJECT_RED_INJURIES.map((injury, i) => ({
      urgency: "RED",
      isAutoGenerated: true,
      injury,
      confidence: Math.min(0.97, 0.88 + i * 0.02),
      signals: "MOTION·AUDIO·VISION",
    }));
    await writeSimulationPackets(blueprints);
  };

  const handleClearAllPackets = async () => {
    if (!db) return;
    setSimBusy(true);
    try {
      const snap = await getDocs(collection(db, "packets"));
      let n = 0;
      for (const d of snap.docs) {
        await deleteDoc(doc(db, "packets", d.id));
        n += 1;
      }
      pushToast(`Cleared ${n} packet(s)`, "YELLOW");
    } catch (e) {
      console.error(e);
      pushToast("Clear failed", "RED");
    } finally {
      setSimBusy(false);
    }
  };

  const handleSimulateAutoSos = async () => {
    if (!db) return;
    setSimBusy(true);
    try {
      const loc = jitterSimulationCoords();
      const ref = await addDoc(collection(db, "packets"), {
        urgency: "RED",
        isAutoGenerated: true,
        injury: "Auto-detected collapse — LSTM confidence 94%",
        confidence: 0.94,
        signals: "MOTION·AUDIO·VISION",
        createdAt: serverTimestamp(),
        lat: loc.lat,
        lon: loc.lon,
      });
      pushToast("Auto-SOS packet written — responder update in 3s", "RED");
      setTimeout(async () => {
        try {
          await updateDoc(doc(db, "packets", ref.id), {
            responderConfirmation: "Responder ACK · ETA 4 min · Unit MED-12",
            responderConfirmedAt: serverTimestamp(),
          });
          pushToast("Responder confirmation applied", "GREEN");
        } catch (err) {
          console.error(err);
          pushToast("Responder confirmation failed", "RED");
        }
      }, 3000);
    } catch (e) {
      console.error(e);
      pushToast("Auto-SOS simulation failed", "RED");
    } finally {
      setSimBusy(false);
    }
  };

  const sendPacket = async (urgency, isAutoGenerated = false) => {
    const location = await getLocation();
    const payload = buildPacket(urgency, isAutoGenerated, location);
    const docRef = await addDoc(collection(db, "packets"), payload);
    const meta = {
      id: docRef.id,
      urgency,
      isAutoGenerated,
      sentAt: new Date().toLocaleTimeString(),
    };
    setLastSent(meta);
    pushToast(
      `${urgency} packet sent${isAutoGenerated ? " (AUTO-SOS)" : ""}`,
      urgency,
    );
    return meta;
  };

  const handleSosClick = async () => {
    if (sosState !== "idle") return;
    setSosState("triggered");
    try {
      await sendPacket("RED", true);
      setSosState("sent");
      if (sosResetRef.current) clearTimeout(sosResetRef.current);
      sosResetRef.current = setTimeout(() => {
        setSosState("idle");
        sosResetRef.current = null;
      }, 3000);
    } catch {
      setSosState("idle");
    }
  };

  const ringStateClass =
    sosState === "idle" ? "" : sosState === "triggered" ? "triggered" : "sent";

  const criticalCount = packets.filter((p) => p.urgency === "RED").length;

  return (
    <div className="victim-panel-root">
      <div className="victim-panel-grid victim-light">
        <div className={`sos-ring-system ${ringStateClass}`}>
          <div className="sos-ring-el ring-1" aria-hidden />
          <div className="sos-ring-el ring-2" aria-hidden />
          <div className="sos-ring-el ring-3" aria-hidden />
          <div className="victim-hub-logo victim-hub-logo-mesh" aria-hidden>
            <svg className="victim-hub-mesh-svg" viewBox="0 0 64 64" role="img">
              <title>OmniMesh mesh network</title>
              <defs>
                <radialGradient id="hubMeshGrad" cx="42%" cy="35%" r="65%">
                  <stop offset="0%" stopColor="#4285F4" />
                  <stop offset="100%" stopColor="#34A853" />
                </radialGradient>
              </defs>
              <g className="victim-hub-mesh-rotate">
                <g stroke="#DADCE0" strokeWidth="1.8" strokeLinecap="round">
                  {[
                    [32, 14],
                    [47.5, 23],
                    [47.5, 41],
                    [32, 50],
                    [16.5, 41],
                    [16.5, 23],
                  ].map(([x, y], i, arr) => {
                    const [nx, ny] = arr[(i + 1) % arr.length];
                    return (
                      <line
                        key={`e-${i}`}
                        x1={x}
                        y1={y}
                        x2={nx}
                        y2={ny}
                        opacity={0.55}
                      />
                    );
                  })}
                  {[
                    [32, 14],
                    [47.5, 23],
                    [47.5, 41],
                    [32, 50],
                    [16.5, 41],
                    [16.5, 23],
                  ].map(([x, y], i) => (
                    <line
                      key={`s-${i}`}
                      x1={32}
                      y1={32}
                      x2={x}
                      y2={y}
                      opacity={0.35}
                      strokeWidth={1.2}
                    />
                  ))}
                </g>
                {[
                  [32, 14, "#4285F4"],
                  [47.5, 23, "#34A853"],
                  [47.5, 41, "#FBBC04"],
                  [32, 50, "#EA4335"],
                  [16.5, 41, "#4285F4"],
                  [16.5, 23, "#34A853"],
                ].map(([cx, cy, fill], i) => (
                  <circle
                    key={i}
                    cx={cx}
                    cy={cy}
                    r={3.6}
                    fill={fill}
                    opacity={0.92}
                  >
                    <animate
                      attributeName="opacity"
                      values="0.78;1;0.78"
                      dur="2.6s"
                      repeatCount="indefinite"
                      begin={`${i * 0.22}s`}
                    />
                  </circle>
                ))}
              </g>
              <circle cx="32" cy="32" r="8.2" fill="url(#hubMeshGrad)" />
              <circle cx="29" cy="29" r="2.2" fill="#FFFFFF" opacity={0.45} />
            </svg>
          </div>
          <div className="victim-status-copy">
            <div className="victim-status-title">OMNIMESH ACTIVE.</div>
            <div className="victim-status-sub">
              MESH CONNECTED: {peerCount.toLocaleString()} PEERS.
            </div>
          </div>
        </div>

        <section className="manual-triage-card">
          <div className="manual-triage-head">
            <span className="manual-triage-title">
              AUTOMATED DISTRESS DETECTION:
            </span>
            <span className="manual-triage-title" style={{ color: "#34A853" }}>
              ON
            </span>
          </div>
          <div className="sensor-indicators-row" aria-hidden>
            <div className="sensor-tile sensor-tile--motion">
              <svg
                className="sensor-icon"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <rect x="5" y="2" width="14" height="20" rx="2" />
                <line
                  x1="12"
                  y1="18"
                  x2="12"
                  y2="18.01"
                  strokeWidth="3"
                  strokeLinecap="round"
                />
              </svg>
              <span className="sensor-label">Accelerometer</span>
            </div>
            <div className="sensor-tile sensor-tile--audio">
              <svg
                className="sensor-icon"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z" />
                <path d="M19 10v2a7 7 0 0 1-14 0v-2M12 19v4M8 23h8" />
              </svg>
              <span className="sensor-label">Microphone</span>
            </div>
          </div>
          <div className="sensor-channel-accent" aria-hidden />
          <div className="fusion-bar fusion-bar-quad" aria-hidden>
            <span style={{ background: "#4285F4" }} />
            <span style={{ background: "#EA4335" }} />
            <span style={{ background: "#FBBC04" }} />
            <span style={{ background: "#34A853" }} />
          </div>
        </section>

        <button
          type="button"
          className={`report-emergency-btn ${sosState}`}
          onClick={handleSosClick}
          disabled={sosState !== "idle"}
        >
          {sosState === "sent"
            ? "✓ SENT"
            : sosState === "triggered"
              ? "SENDING..."
              : "REPORT EMERGENCY"}
        </button>

        <details className="victim-manual-details">
          <summary className="victim-manual-summary">
            Manual triage entry
          </summary>
          <section className="manual-triage-card victim-manual-inner">
            <div className="manual-triage-head">
              <span className="manual-triage-title">MANUAL TRIAGE ENTRY</span>
              <span
                className="manual-triage-hint"
                title="Use only when automated fusion is unavailable"
              >
                ⓘ
              </span>
            </div>
            <div className="manual-btn-row">
              <button
                type="button"
                className="manual-triage-btn red"
                onClick={() => sendPacket("RED", false)}
              >
                + CRITICAL
              </button>
              <button
                type="button"
                className="manual-triage-btn yellow"
                onClick={() => sendPacket("YELLOW", false)}
              >
                + SERIOUS
              </button>
              <button
                type="button"
                className="manual-triage-btn green"
                onClick={() => sendPacket("GREEN", false)}
              >
                + MINOR
              </button>
            </div>
            <div className="manual-combo-row">
              <button
                type="button"
                className="manual-combo-btn"
                onClick={async () => {
                  await sendPacket("YELLOW", false);
                  await sendPacket("GREEN", false);
                }}
              >
                SEND YELLOW + GREEN PAIR
              </button>
            </div>
          </section>
        </details>

        {criticalCount > 0 ? (
          <button
            type="button"
            className="nearby-card"
            onClick={onSwitchToField}
          >
            <span className="nearby-icon">!</span>
            <span className="nearby-copy">
              <span>Nearby Triage:</span>
              <span>{criticalCount} Critical Cases detected.</span>
            </span>
            <span className="nearby-chevron">›</span>
          </button>
        ) : null}

        {demoMode ? (
          <details className="victim-simulation-details">
            <summary className="victim-simulation-summary">SIMULATION CONTROLS</summary>
            <div className="victim-simulation-inner">
              {simBusy ? (
                <p className="victim-simulation-status" role="status">
                  Injecting…
                </p>
              ) : null}
              <div className="victim-simulation-grid">
                <button
                  type="button"
                  className="victim-sim-btn victim-sim-btn--earthquake"
                  disabled={simBusy}
                  onClick={handleEarthquakeScenario}
                >
                  <span className="victim-sim-icon" aria-hidden>
                    ⚡
                  </span>
                  EARTHQUAKE SCENARIO
                </button>
                <button
                  type="button"
                  className="victim-sim-btn victim-sim-btn--flood"
                  disabled={simBusy}
                  onClick={handleFloodScenario}
                >
                  <span className="victim-sim-icon" aria-hidden>
                    ⚡
                  </span>
                  FLOOD SCENARIO
                </button>
                <button
                  type="button"
                  className="victim-sim-btn victim-sim-btn--inject-red"
                  disabled={simBusy}
                  onClick={handleInject5Red}
                >
                  <span className="victim-sim-icon" aria-hidden>
                    ⚡
                  </span>
                  INJECT 5 RED
                </button>
                <button
                  type="button"
                  className="victim-sim-btn victim-sim-btn--clear"
                  disabled={simBusy}
                  onClick={handleClearAllPackets}
                >
                  <span className="victim-sim-icon" aria-hidden>
                    ⚡
                  </span>
                  CLEAR ALL PACKETS
                </button>
              </div>
              <button
                type="button"
                className="victim-sim-btn victim-sim-btn--autosos victim-sim-btn--full"
                disabled={simBusy}
                onClick={handleSimulateAutoSos}
              >
                <span className="victim-sim-icon" aria-hidden>
                  ⚡
                </span>
                SIMULATE AUTO-SOS
              </button>
            </div>
          </details>
        ) : null}

        {lastSent ? (
          <div className="victim-meta-card">
            Last: <strong>{lastSent.urgency}</strong> ({lastSent.id.slice(0, 8)}
            ){lastSent.isAutoGenerated ? " · AUTO-SOS" : " · MANUAL"} ·{" "}
            {lastSent.sentAt}
          </div>
        ) : null}

        <div className="victim-panel-fill" aria-hidden />
      </div>

      <div className="victim-toast-stack" aria-live="polite">
        {toasts.map((t) => (
          <div
            key={t.id}
            className={`victim-toast-item ${t.urgency === "RED" ? "red" : t.urgency === "YELLOW" ? "yellow" : "green"}`}
          >
            {t.message}
          </div>
        ))}
      </div>
    </div>
  );
}
